import { point as turfPoint } from '@turf/turf';
import chroma from 'chroma-js';
import { nanoid } from 'nanoid';
import { createCircleFromPoint } from '~/common/geoUtils';
import { BRIGHT_COLORS } from '~/constants/color';
import { calculateArea } from '~/helpers/unitHelpers';
import CanvasObject from '../CanvasObject';
import { opacityWhileDragging } from '../common';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import VertexObject from '../VertexObject';
import { FONT_SIZE, getCircleDestCoords, LABEL_OFFSET, LABEL_PADDING } from './common';

type UpdaterFn = (center: LngLat) => void;
const PADDING = 10; // pixels
const HANDLE_WIDTH = 5; // pixels
const HANDLE_RAD = HANDLE_WIDTH / 2;

export default class EditCircleObject extends CanvasObject {
  dragOffset: Point = { x: NaN, y: NaN };
  center: VertexObject;
  _onCircleUpdated: UpdaterFn;

  constructor(id: string, surface: SurfaceBase, onCircleUpdated: UpdaterFn) {
    super(id, surface);
    this.interactive = true;
    this._onCircleUpdated = onCircleUpdated;
    const circle = this.getCurrentCircleObject();
    if (!circle) throw new Error('currentObject is not a circle!');

    const [lng, lat] = circle.geometry.coordinates;

    this.center = this.createVertex({ lng, lat });

    this.addChild(this.center);

    this.on('hoverStart', () => {
      this.surface.setCursor('grab');
    });

    this.on('hoverEnd', () => {
      this.surface.resetCursor();
    });

    this.on('dragStart', (e) => {
      this.surface.setCursor('grabbing');
      this.center.visible = false;
      this.resetDragOffset();
      this.dragOffset = e.diff();
      this.renderAll();
    });

    this.on('dragging', (e) => {
      this.dragOffset = e.diff();
      this.renderAll();
    });

    this.on('dragEnd', (e) => {
      this.surface.resetCursor();
      this.dragOffset = e.diff();
      this.updateVertexPoint(this.center, e.diff());
      this.center.visible = true;
      this.resetDragOffset();
      this.onCircleUpdated();
      this.renderAll();
    });
  }

  getCurrentCircleObject() {
    const currentObject = this.getStore().currentObject;
    if (currentObject?.type !== 'circle') return;
    return currentObject;
  }

  get isDragging() {
    return !Number.isNaN(this.dragOffset.x);
  }

  get outerPoint() {
    const circle = this.getCurrentCircleObject();
    if (!circle) throw new Error('currentObject is not a circle!');
    const { coordinates } = getCircleDestCoords({
      lngLat: this.center.lngLat,
      radius: circle.properties.radius,
    }).geometry;

    return this.surface.toScreenCoords({
      lng: coordinates[0],
      lat: coordinates[1],
    });
  }

  get radiusInPixels() {
    return Math.abs(this.outerPoint.x - this.center.point.x);
  }

  get bbox() {
    const { x, y } = this.center.point;
    const r = this.radiusInPixels;
    const br = r + PADDING;

    const p1: Point = { x: x - br, y: y - br };
    const p2: Point = { x: x - br, y: y + br };
    const p3: Point = { x: x + br, y: y + br };
    const p4: Point = { x: x + br, y: y - br };

    return [p1, p2, p3, p4];
  }

  onCircleUpdated() {
    this._onCircleUpdated(this.center.lngLat);
  }

  createVertex(lngLat: LngLat) {
    const v = new VertexObject(nanoid(), this.surface, {
      lngLat,
      point: this.surface.toScreenCoords(lngLat),
    });

    return v;
  }

  updateVertexPoint(v: VertexObject, diff: Point) {
    v.point.x += diff.x;
    v.point.y += diff.y;
    v.refreshMapCoords();
  }

  resetDragOffset() {
    this.dragOffset = { x: NaN, y: NaN };
  }

  bboxHandle(ctx: CanvasRenderingContext2D, p: Point) {
    ctx.fillRect(p.x - HANDLE_RAD, p.y - HANDLE_RAD, HANDLE_WIDTH, HANDLE_WIDTH);
  }

  setOpacityWhileDragging = (ctx: CanvasRenderingContext2D) => {
    if (ctx.globalAlpha > opacityWhileDragging) {
      ctx.globalAlpha = opacityWhileDragging;
    }
  };

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D, color: string): void {
    if (!this.getCurrentCircleObject()) return;
    // render entire bbox rectangle as hit area when editing
    const [p1, _, p3, __] = this.bbox;

    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.fillRect(p1.x, p1.y, p3.x - p1.x, p3.y - p1.y);
    ctx.restore();
  }

  render(ctx: CanvasRenderingContext2D): void {
    const circle = this.getCurrentCircleObject();
    if (!circle) return;
    const { properties } = circle;
    const { x, y } = this.center.point;
    const r = this.radiusInPixels;
    const { strokeStyle: lineDash, color, strokeWidth, strokeOpacity, fillOpacity } = properties;

    ctx.globalAlpha = 1;
    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = color;
    ctx.fillStyle = chroma(color).alpha(fillOpacity).hex();
    lineDash === 'dashed' && ctx.setLineDash([10, 4]);

    if (this.isDragging) {
      this.setOpacityWhileDragging(ctx);
      ctx.beginPath();
      ctx.arc(x + this.dragOffset.x, y + this.dragOffset.y, r, 0, 2 * Math.PI);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      return;
    }

    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = strokeOpacity;
    ctx.stroke();

    ctx.font = `bold ${FONT_SIZE}px Inter`;

    if (properties.showArea) {
      // render labels
      const { lng, lat } = this.center.lngLat;

      const circlePoly = createCircleFromPoint({
        center: turfPoint([lng, lat]).geometry,
        radius: properties.radius,
      });

      const a = calculateArea({
        unitSystem: this.getStore().currentUnitSystem,
        geometry: circlePoly,
      });

      const areaText = `${a.value.toLocaleString()} ${a.unit}`;
      const areaTextWidth = ctx.measureText(areaText).width;
      const circleWidth = r * 2;

      const exceedsWidth = areaTextWidth + LABEL_PADDING > circleWidth;

      if (!exceedsWidth) {
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.translate(x, y);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.roundRect(
          -areaTextWidth / 2 - LABEL_PADDING / 2,
          -FONT_SIZE - LABEL_OFFSET,
          areaTextWidth + LABEL_PADDING,
          FONT_SIZE + LABEL_PADDING,
          [5],
        );
        ctx.fill();

        ctx.fillStyle = BRIGHT_COLORS.includes(color) ? '#000000' : '#FFFFFF';
        ctx.textBaseline = 'top';
        ctx.textAlign = 'center';
        ctx.fillText(areaText, 0, -FONT_SIZE / 2 - LABEL_OFFSET - LABEL_PADDING / 4);
        ctx.restore();
      }
    }

    if (properties.showRadius) {
      // show radius too
      const radiusInMiles = (properties.radius / (properties.useMilesToDisplayRadius ? 5_280 : 1)).toLocaleString();
      const radiusText = `R= ${radiusInMiles} ${properties.useMilesToDisplayRadius ? 'mi' : 'ft'}`;
      const radiusTextWidth = ctx.measureText(radiusText).width;

      const radTextExceeds = radiusTextWidth + LABEL_PADDING > r;
      if (!radTextExceeds) {
        const midPoint: Point = {
          x: x + (this.outerPoint.x - x) / 2,
          y: y + (this.outerPoint.y - y) / 2,
        };

        // draw rad line
        ctx.save();
        ctx.lineWidth = 2;
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(this.outerPoint.x, this.outerPoint.y);
        ctx.stroke();
        ctx.restore();

        ctx.save();
        ctx.globalAlpha = 1;
        ctx.translate(midPoint.x, midPoint.y);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.roundRect(
          -radiusTextWidth / 2 - LABEL_PADDING / 2,
          LABEL_OFFSET / 2,
          radiusTextWidth + LABEL_PADDING,
          FONT_SIZE + LABEL_PADDING,
          [5],
        );
        ctx.fill();

        ctx.fillStyle = BRIGHT_COLORS.includes(color) ? '#000000' : '#FFFFFF';
        ctx.textBaseline = 'top';
        ctx.textAlign = 'center';
        ctx.fillText(radiusText, 0, FONT_SIZE / 2 + LABEL_OFFSET / 4 + LABEL_PADDING / 4);
        ctx.restore();
      }
    }

    const [p1, p2, p3, p4] = this.bbox;

    // render bbox
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.fillStyle = color;

    ctx.beginPath();
    ctx.rect(p1.x, p1.y, p3.x - p1.x, p3.y - p1.y);
    ctx.stroke();

    this.bboxHandle(ctx, p1);
    this.bboxHandle(ctx, p2);
    this.bboxHandle(ctx, p3);
    this.bboxHandle(ctx, p4);

    ctx.restore();
  }
}
